import React, { useState, useCallback, useEffect } from 'react';
import { FiFilter, FiX } from 'react-icons/fi';
import { CONTRACT_STATUS_OPTIONS, CONTENT_TYPE_OPTIONS } from './constants';

const PROFILE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'complete', label: 'Complete' },
  { value: 'incomplete', label: 'Incomplete' },
];

const FOLLOWERS_PRESETS = [
  { label: 'Any', min: '', max: '' },
  { label: '0 - 1K', min: 0, max: 1000 },
  { label: '1K - 10K', min: 1000, max: 10000 },
  { label: '10K - 50K', min: 10000, max: 50000 },
  { label: '50K - 100K', min: 50000, max: 100000 },
  { label: '100K+', min: 100000, max: '' },
];

const defaultFilters = {
  profileComplete: '',
  contractStatus: '',
  contentType: [],
  followersMin: '',
  followersMax: '',
};

/**
 * Filter section for Users Management. Matches dashboard layout: bg-white, rounded-lg, shadow.
 */
const UsersFilter = ({ filters = defaultFilters, onChange, onClear }) => {
  const [expanded, setExpanded] = useState(false);
  const [localProfile, setLocalProfile] = useState(filters.profileComplete ?? '');
  const [localContract, setLocalContract] = useState(filters.contractStatus ?? '');
  const [localContentTypes, setLocalContentTypes] = useState(
    Array.isArray(filters.contentType) ? [...filters.contentType] : []
  );
  const [localFollowersMin, setLocalFollowersMin] = useState(
    filters.followersMin !== undefined && filters.followersMin !== '' ? String(filters.followersMin) : ''
  );
  const [localFollowersMax, setLocalFollowersMax] = useState(
    filters.followersMax !== undefined && filters.followersMax !== '' ? String(filters.followersMax) : ''
  );

  useEffect(() => {
    setLocalProfile(filters.profileComplete ?? '');
    setLocalContract(filters.contractStatus ?? '');
    setLocalContentTypes(Array.isArray(filters.contentType) ? [...filters.contentType] : []);
    setLocalFollowersMin(
      filters.followersMin !== undefined && filters.followersMin !== '' ? String(filters.followersMin) : ''
    );
    setLocalFollowersMax(
      filters.followersMax !== undefined && filters.followersMax !== '' ? String(filters.followersMax) : ''
    );
  }, [filters.profileComplete, filters.contractStatus, filters.contentType, filters.followersMin, filters.followersMax]);

  const hasActiveFilters =
    localProfile !== '' ||
    localContract !== '' ||
    localContentTypes.length > 0 ||
    localFollowersMin !== '' ||
    localFollowersMax !== '';

  const applyFilters = useCallback(() => {
    const min = localFollowersMin === '' ? '' : parseInt(localFollowersMin, 10);
    const max = localFollowersMax === '' ? '' : parseInt(localFollowersMax, 10);
    onChange({
      profileComplete: localProfile,
      contractStatus: localContract,
      contentType: localContentTypes,
      followersMin: min === '' || Number.isNaN(min) ? '' : min,
      followersMax: max === '' || Number.isNaN(max) ? '' : max,
    });
  }, [
    localProfile,
    localContract,
    localContentTypes,
    localFollowersMin,
    localFollowersMax,
    onChange,
  ]);

  const handleClear = useCallback(() => {
    setLocalProfile('');
    setLocalContract('');
    setLocalContentTypes([]);
    setLocalFollowersMin('');
    setLocalFollowersMax('');
    onClear?.();
  }, [onClear]);

  const toggleContentType = (value) => {
    setLocalContentTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const setFollowersPreset = (preset) => {
    setLocalFollowersMin(preset.min === '' ? '' : String(preset.min));
    setLocalFollowersMax(preset.max === '' ? '' : String(preset.max));
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-100">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50/80 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FiFilter className="w-5 h-5 text-gray-500" />
          <span className="font-medium text-gray-800">Filters</span>
          {hasActiveFilters && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              Active
            </span>
          )}
        </div>
        <span className="text-sm text-gray-500">
          {expanded ? 'Collapse' : 'Expand'}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/50 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Profile (complete / incomplete) */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                Profile
              </label>
              <select
                value={localProfile}
                onChange={(e) => setLocalProfile(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm text-gray-800 bg-white"
              >
                {PROFILE_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Contract status */}
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                Contract status
              </label>
              <select
                value={localContract}
                onChange={(e) => setLocalContract(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm text-gray-800 bg-white"
              >
                <option value="">All</option>
                {CONTRACT_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Followers range */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                Followers range
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {FOLLOWERS_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => setFollowersPreset(preset)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      localFollowersMin === (preset.min === '' ? '' : String(preset.min)) &&
                      localFollowersMax === (preset.max === '' ? '' : String(preset.max))
                        ? 'bg-orange-500 text-white'
                        : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="number"
                  min={0}
                  placeholder="Min"
                  value={localFollowersMin}
                  onChange={(e) => setLocalFollowersMin(e.target.value)}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                />
                <span className="text-gray-400">–</span>
                <input
                  type="number"
                  min={0}
                  placeholder="Max"
                  value={localFollowersMax}
                  onChange={(e) => setLocalFollowersMax(e.target.value)}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Content types (multi-select chips) */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Content types
            </label>
            <div className="flex flex-wrap gap-2">
              {CONTENT_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleContentType(opt.value)}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    localContentTypes.includes(opt.value)
                      ? 'bg-orange-500 text-white'
                      : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={applyFilters}
              className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 font-medium text-sm transition-colors"
            >
              Apply filters
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors"
            >
              <FiX className="w-4 h-4" />
              Clear all
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersFilter;
